/**
 * @email       : saraman@ibl.mx
 * @author      : Arman Ali
 * @title       : Full Stack Developer
 * @github      : https://github.com/Arman-Arzoo
 * @component   : ApprovalFlow
 * @description : Component for managing the approval flow UI, including selecting departments,
 *                modules, and managing approval levels. It allows adding and removing approvers
 *                and approval levels, and saving the approval flow configuration through an API.
 *
 * @dependencies :
 * - React (for component rendering)
 * - Redux (for managing state)
 * - Custom components (ButtonAction, CustomSelect, CustomTextField, etc.)
 * - i18next (for translations)
 * - Helper functions (for permissions and routes)
 *
 * @returns {JSX.Element} The ApprovalFlow component which renders a form for setting up approval flows,
 *                         with dynamic approval level management and API interaction.
 */

import ButtonAction from '../../components/buttons/ButtonAction';
import ButtonIcon from '../../components/buttons/ButtonIcon';
import {AddIcon, TrashOutlineIcon, EditIcon, CleanIcon, PersonChipBlack} from '../../components/buttons/IconButtons';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomTextField } from '../../components/CustomTextField';
import DivContainerBody from '../../components/DivContainerBody';
import DividerWithText from '../../components/DividerWithText';
import HeadTittle from '../../components/HeadTittle';
import NotInformation from '../../components/NotInformation';
import routes from '../../Routes/routes';
import ApprovalFlowApp from './ApprovalFlowApp';
import { SCOPE_MODEL} from "./Model/Model";
import CustomButtonLink from "../../components/buttons/CustomButtonLink";
import DeleteModal from "../Modals/DeleteModal";
import {getModelKeyById} from "../../Apis/Models";
import CustomAddChipSearch from "../../components/CustomAddChipSearch";
import {USER_TYPE_MODEL} from "../../enums/userTypeEnum";
import React from "react";
import {userListToLabelEmailPhoneValue} from "../../Apis/functions";

const ApprovalFlow = () => {
    const {
        emptyFields,
        t,
        setDeleteModal,
        levels,
        deleteId,
        setDeleteId,
        deleteModal,
        getAllDepartments,
        getListApprovalModules,
        approvalId,
        isDetails,
        canEdit,
        item,
        handleChange,
        cleanChipSearch,
        setCleanChipSearch,
        deleteApprovalLevel,
        handleApprovalFlowApi,
        handleRuleChange,
        addApprover,
        addApprovalLevel,
        handleClear
    } = ApprovalFlowApp();

    return (
        <>
            <HeadTittle
                name={isDetails ? 'details' : approvalId ? 'update' : 'create'}
                link={routes.approval.list}
                divStack={
                !approvalId &&
                <ButtonIcon
                    id='btn_af_clean'
                    Icon={CleanIcon}
                    onClick={handleClear}
                    title={'clear_instances'}
                    className={"btn-clean-secondary-600"}
                    tooltipPlacement={"right"}
                    iconProps={'btn-icon-svg-12px icon-color-general-00'}
                />

            }>
                {canEdit && (
                        <ButtonAction
                            id='btn_af_add_approval'
                            name={approvalId ? 'update' : 'create'}
                            Icon={approvalId ? EditIcon: AddIcon}
                            className='btn-icon-add'
                            onClick={() => handleApprovalFlowApi()}
                        />
                )}
            </HeadTittle>
            <DivContainerBody>
                <div className={"row"}>
                    <div className={'col-12 my-4'}>
                        <DividerWithText title={t('data')} />
                    </div>

                    <div className='col-12'>
                        <CustomTextField
                            id={'txt_af_approval_flow_name'}
                            length={128}
                            name={'approvalFlowName'}
                            value={item.approvalFlowName}
                            trans={'name'}
                            type={'text'}
                            onChange={handleChange}
                            emptyFields={emptyFields}
                            required={true}
                            disabled={!canEdit}
                        />
                    </div>
                    <div className='col-sm-12 col-md-6 col-xxl-6 col-lg-6'>
                        <CustomSelect
                            id={'slt_af_department'}
                            name={'department'}
                            value={item.department}
                            keyName='id'
                            onChange={handleChange}
                            list={getAllDepartments}
                            trans={'department'}
                            label={'slt_af_department'}
                            required={false}
                            disabled={!canEdit}
                        />
                    </div>

                    <div className='col-sm-12 col-md-6 col-xxl-6 col-lg-6'>
                        <CustomSelect
                            id={'slt_af_module'}
                            name={'module'}
                            value={item.module}
                            keyName='id'
                            onChange={handleChange}
                            list={getListApprovalModules.map(({id}) => {
                                return {id:id, name:t(getModelKeyById(id))}
                            })}
                            trans={'module'}
                            label={'slt_af_module'}
                            emptyFields={emptyFields}
                            required={true}
                            showNoSelectionOption={false}
                            disabled={!canEdit}
                        />
                    </div>
                </div>

                <div className='row'>
                    <div className={'col-12 mt-4 mb-4'}>
                        <DividerWithText title={t('levels')} />
                    </div>

                    {levels?.length > 0 ? (
                        <>
                            {canEdit &&
                                <div className='col-12 mb-4'>
                                    <ButtonAction
                                        id='btn_af_create_level'
                                        name='create_new_level'
                                        Icon={AddIcon}
                                        splitName={false}
                                        className='btn-outlined-secondary-600-25'
                                        onClick={addApprovalLevel}
                                    />
                                </div>
                            }
                            { levels?.map((level, index) => (
                                <div key={level?.levelNumber}>
                                    <div className='col-12 mb-4 p-0'>
                                        <div className={"d-flex justify-content-between"}>
                                            <p className={"mr-3"}>{t("level")} {level?.levelNumber}</p>
                                           {canEdit && (
                                            <ButtonIcon
                                                Icon={TrashOutlineIcon}
                                                id={'btn_af_approval_del_'+ index}
                                                title={''}
                                                onClick={() => {
                                                    setDeleteModal(true);
                                                    setDeleteId(level.levelNumber);
                                                }}
                                                iconProps={'btn-icon-svg-white btn-icon-svg-12px'}
                                                className='btn-icon-delete'
                                            />
                                            )}
                                        </div>
                                    </div>
                                    <div className='col-12 p-0'>
                                        <CustomSelect
                                            id={'slt_af_scope_'+ index}
                                            name={'scope'}
                                            value={level?.approvalCondition}
                                            keyName='id'
                                            onChange={(e) => handleRuleChange(level.levelNumber, e.target.value)}
                                            list={SCOPE_MODEL()}
                                            trans={'scope'}
                                            label={'scope'}
                                            emptyFields={emptyFields}
                                            required={true}
                                            showNoSelectionOption={false}
                                            disabled={!canEdit}
                                        />
                                    </div>


                                    <div className='col-12 p-0'>
                                        <CustomAddChipSearch
                                            autocompleteId={'slt_af_employees_'+ index}
                                            buttonId={'btn_af_add_employee_'+ index}
                                            selectedList={Array.isArray(level?.approvers) ? level.approvers : []}
                                            setSelectedList={(newValue) => addApprover(level.levelNumber, newValue)}
                                            userTypeIdsSearch={[
                                                USER_TYPE_MODEL.EMPLOYEE
                                            ]}
                                            disabled={!canEdit}
                                            label="name"
                                            emptyFields={emptyFields}
                                            required={true}
                                            reset={false}
                                            hideButton={false}
                                            keyId={"userId"}
                                            showSubSection={false}
                                            sanitizeDataList={userListToLabelEmailPhoneValue}
                                            Icon={PersonChipBlack}
                                        />
                                    </div>
                                </div>
                            ))}

                        </>
                    ) : (
                        <div className='col-12'>
                            <NotInformation card={true}>
                                <>
                                    <div className="warning-text">
                                        <p>{t("no_approval_levels")}</p>
                                    </div>
                                    {canEdit &&
                                        <CustomButtonLink
                                            splitName={false}
                                            id="btn_af_create"
                                            card={true}
                                            onClick={addApprovalLevel}
                                            name="create_new_level"
                                        />
                                    }
                                </>
                            </NotInformation>
                        </div>
                    )}
                </div>
            </DivContainerBody>

            <DeleteModal
                show={deleteModal}
                onHide={() => setDeleteModal(false)}
                data={[deleteId]}
                onClickFn={() => {
                    deleteApprovalLevel(deleteId);
                    setDeleteModal(false);
                }}
                title_modal={'delete_level'}
                element_modal={'level'}
                isReset={() => setDeleteId(null)}
            />
        </>
    );
};

export default ApprovalFlow;
